import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../../lib/api';
import { tokens as fmt } from '../../lib/format';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { OriginMark } from '../../components/OriginMark';
import { QueuedRequestRow } from '../../components/QueuedRequestRow';
import { StatusPill } from '../../components/StatusPill';
import { Tabs } from '../../components/Tabs';
import { useStore } from '../../store/StoreProvider';
import { sessionMessages, traceBySession } from '../../store/selectors';
import { FilesPanel } from './FilesPanel';
import { PushButton } from './PushButton';
import { PopTownPlanPanel } from './PopTownPlanPanel';
import { PlanPanel } from './PlanPanel';
import { TraceList } from './TraceList';
import { Thread } from './Thread';

const PANEL_KEY = 'poptown.session.panel';

type Tab = 'poptown' | 'piano' | 'traccia' | 'file';

// il Piano di PopTown di «pianifichiamo insieme»
const POPTOWN_PLAN = 'PIANO.md';

export function Session() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const { state, dispatch, openModal, toast } = useStore();
  const [tab, setTab] = useState<Tab>('piano');
  // il pannello Piano/Traccia/File si chiude per dare spazio alla chat; la scelta si ricorda
  const [panelOpen, setPanelOpen] = useState(() => {
    try {
      return localStorage.getItem(PANEL_KEY) !== 'closed';
    } catch {
      return true;
    }
  });
  const togglePanel = (open: boolean) => {
    setPanelOpen(open);
    try {
      localStorage.setItem(PANEL_KEY, open ? 'open' : 'closed');
    } catch {
      // senza storage la scelta vale solo per questa visita
    }
  };
  const [answering, setAnswering] = useState(false);
  const [queueOpen, setQueueOpen] = useState(false);

  // piano e file arrivano dal registro all'apertura; poi li aggiorna lo stream
  useEffect(() => {
    if (!id) return;
    void api.tasks(id).then((tasks) => dispatch({ type: 'tasks/set', sessionId: id, tasks }));
    void api.files(id).then((files) => dispatch({ type: 'files/set', sessionId: id, files }));
  }, [id, dispatch]);

  // un Piano di PopTown appena scritto, prima dei lavori: lo si mostra subito
  const planReady =
    Boolean(state.files[id]?.some((f) => f.path === POPTOWN_PLAN)) && !state.tasks[id]?.length;
  useEffect(() => {
    if (planReady) setTab('poptown');
  }, [planReady]);

  const s = state.sessions.find((x) => x.id === id);
  if (!s) {
    return (
      <div style={{ padding: 40 }}>
        <div className="t-h3" style={{ marginBottom: 8 }}>
          Sessione non trovata.
        </div>
        <Button onClick={() => nav('/')}>Torna alla Dashboard</Button>
      </div>
    );
  }

  const messages = sessionMessages(state, s.id);
  const trace = traceBySession(state, s.id);
  const tasks = state.tasks[s.id] ?? [];
  const files = state.files[s.id] ?? [];
  const popTownPlan = files.find((f) => f.path === POPTOWN_PLAN)?.content ?? '';

  // un riferimento a un agente porta alla traccia, dove c'è quello che ha fatto
  const focusNode = () => setTab('traccia');

  // ottimistico con riconciliazione per id e rollback completo (§F4)
  const answer = async (choice: string) => {
    setAnswering(true);
    dispatch({ type: 'session/optimistic', id: s.id });
    dispatch({ type: 'session/patch', id: s.id, patch: { pending: null, state: 'running' } });
    const tempId = 'm-opt-' + Date.now();
    dispatch({
      type: 'message/add',
      message: {
        id: tempId,
        sessionId: s.id,
        role: 'user',
        kind: 'request',
        text: choice,
        deliveredVia: ['text'],
        at: new Date().toISOString(),
      },
    });
    try {
      const r = await api.reply(s.id, { choice });
      dispatch({ type: 'message/settle', tempId, id: r.messageId ?? null });
      dispatch({ type: 'session/settle', id: s.id });
      toast({ kind: 'success', title: 'Risposta registrata', body: choice });
    } catch {
      dispatch({ type: 'message/settle', tempId, id: null });
      dispatch({ type: 'session/rollback', id: s.id });
      toast({
        kind: 'danger',
        title: 'Non è stato possibile registrare la risposta.',
        body: 'Riprova.',
      });
    } finally {
      setAnswering(false);
    }
  };

  // il messaggio entra nel thread quando il server lo registra: niente risposte inventate
  const send = async (text: string, profile?: string) => {
    try {
      await api.reply(s.id, { text, profile });
    } catch (e) {
      toast({
        kind: 'danger',
        title: 'Messaggio non inviato',
        body: e instanceof ApiError && e.detail ? e.detail : 'Riprova fra poco.',
      });
    }
  };

  const cancel = () =>
    openModal({
      kind: 'confirm',
      title: 'Annullare la sessione?',
      okLabel: 'Annulla la sessione',
      body: `Annullare la sessione «${s.title}»?`,
      payload: {
        run: async () => {
          await api.cancel(s.id);
          dispatch({
            type: 'session/patch',
            id: s.id,
            patch: { state: 'cancelled', pending: null },
          });
          toast({ kind: 'info', title: 'Sessione annullata', body: s.title });
        },
      },
    });

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh / var(--zoom) - 56px)',
      }}
    >
      <div
        style={{
          // su telefono va a capo invece di far scorrere la pagina di lato
          minHeight: 52,
          flex: 'none',
          borderBottom: '1px solid var(--border)',
          background: 'var(--panel)',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '6px 12px',
          padding: '6px 20px',
        }}
      >
        <button
          onClick={() => nav('/')}
          className="t-caption dim"
          style={{
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            whiteSpace: 'nowrap',
          }}
        >
          <Icon name="arrow-left" size={14} />
          Dashboard
        </button>
        <span style={{ width: 1, height: 20, background: 'var(--border)' }} />
        <span
          style={{ width: 6, height: 6, borderRadius: '50%', background: s.color, flex: 'none' }}
        />
        <h2 className="t-h2 trunc" style={{ minWidth: 0 }}>
          {s.title}
        </h2>
        <Icon
          name={
            s.origin === 'voice'
              ? 'mic'
              : s.origin === 'schedule'
                ? 'clock'
                : s.origin === 'trigger'
                  ? 'zap'
                  : 'keyboard'
          }
          size={14}
          color="var(--faint)"
        />
        <StatusPill state={s.state} />
        {s.improvised && <OriginMark kind="improvised" reason={s.improvisedReason ?? ''} />}
        <span style={{ flex: 1 }} />
        <span className="t-num dim" style={{ whiteSpace: 'nowrap' }}>
          {fmt(s.tokens)} token
        </span>
        <Button
          variant="ghost"
          icon="external-link"
          onClick={() =>
            openModal({
              kind: 'json',
              title: 'Dettaglio JSON',
              payload: { id: s.id, json: JSON.stringify(s, null, 2) },
            })
          }
        >
          JSON
        </Button>
        {state.role !== 'viewer' && !['completed', 'cancelled', 'failed'].includes(s.state) && (
          <Button variant="ghost" onClick={cancel}>
            Annulla
          </Button>
        )}
      </div>

      {s.queued.length > 0 && (
        <div
          style={{
            flex: 'none',
            background: 'var(--panel-2)',
            borderBottom: '1px solid var(--border)',
            padding: '0 20px',
          }}
        >
          <button
            onClick={() => setQueueOpen(!queueOpen)}
            style={{
              height: 36,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              width: '100%',
              color: 'var(--dim)',
            }}
          >
            <span
              style={{
                display: 'grid',
                transform: queueOpen ? 'rotate(90deg)' : 'none',
                transition: 'transform var(--dur-2)',
              }}
            >
              <Icon name="chevron-right" size={14} />
            </span>
            <span className="t-caption" style={{ flex: 1, textAlign: 'left' }}>
              {s.queued.length} richieste in coda
            </span>
            <Icon name="list-ordered" size={14} color="var(--faint)" />
          </button>
          {queueOpen && (
            <div style={{ padding: '0 0 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {s.queued.map((q, i) => (
                <QueuedRequestRow key={q.id} q={q} n={i + 1} />
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ flex: 1, display: 'flex', minHeight: 0, position: 'relative' }}>
        <Thread
          session={s}
          messages={messages}
          pending={s.pending ?? null}
          answering={answering}
          onAnswer={answer}
          onSend={(t, profile) => void send(t, profile)}
          onFocusNode={focusNode}
          onReopen={() => openModal({ kind: 'new-request', title: 'Nuova richiesta' })}
          wide={!panelOpen}
        />

        {!panelOpen && (
          <button
            type="button"
            className="session-panel-open"
            aria-label="Apri Piano, Traccia e File"
            title="Apri Piano, Traccia e File"
            onClick={() => togglePanel(true)}
          >
            <span style={{ display: 'grid', transform: 'rotate(180deg)' }}>
              <Icon name="chevron-right" size={16} />
            </span>
          </button>
        )}

        <div
          style={{
            flex: 1,
            minWidth: 0,
            background: 'var(--bg)',
            display: panelOpen ? 'flex' : 'none',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <div
            style={{
              height: 36,
              flex: 'none',
              padding: '0 20px',
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              borderBottom: '1px solid var(--border)',
            }}
          >
            <div style={{ flex: 1 }}>
              <Tabs
                items={[
                  ...(popTownPlan ? [{ id: 'poptown', label: 'Piano di PopTown' }] : []),
                  { id: 'piano', label: 'Piano', count: tasks.length },
                  { id: 'traccia', label: 'Traccia', count: trace.length },
                  { id: 'file', label: 'File', count: files.length },
                ]}
                active={tab}
                onChange={(t) => setTab(t as Tab)}
              />
            </div>
            <PushButton sessionId={s.id} projectId={s.project} />
            <button
              type="button"
              className="session-panel-close"
              aria-label="Chiudi il pannello"
              title="Chiudi il pannello: la chat prende tutta la larghezza"
              onClick={() => togglePanel(false)}
            >
              <Icon name="chevron-right" size={16} />
            </button>
          </div>

          <div
            style={{
              flex: 1,
              overflowY: 'auto',
            }}
          >
            {tab === 'poptown' && popTownPlan && (
              <PopTownPlanPanel
                doc={popTownPlan}
                pending={s.pending ?? null}
                busy={answering}
                onAnswer={answer}
              />
            )}
            {tab === 'traccia' && <TraceList trace={trace} />}
            {tab === 'piano' && <PlanPanel tasks={tasks} />}
            {tab === 'file' && <FilesPanel files={files} />}
          </div>
        </div>
      </div>
    </div>
  );
}
